// Small behaviours shared by every page: the main menu, links to collapsed sections,
// and the enquiry form. Each part also responds to Shopify's theme editor, which
// reloads sections in place while the site is being edited.

/* Main menu: the Counselling dropdown and the phone menu ------------------- */

function initHeader() {
  const header = document.querySelector('[data-header]:not([data-ready])');
  if (!header) return;
  header.setAttribute('data-ready', '');

  header.querySelectorAll('[data-menu-toggle]').forEach((button) => {
    const menu = document.getElementById(button.getAttribute('aria-controls') || '');
    const item = button.closest('li');
    if (!menu || !item) return;

    const setOpen = (open) => {
      button.setAttribute('aria-expanded', String(open));
      menu.hidden = !open;
    };

    button.addEventListener('click', () => setOpen(button.getAttribute('aria-expanded') !== 'true'));
    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) {
        setOpen(false);
        button.focus();
      }
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) setOpen(false);
    });
    document.addEventListener('click', (event) => {
      if (item.isConnected && !item.contains(event.target)) setOpen(false);
    });
  });

  const toggle = header.querySelector('[data-mobile-toggle]');
  const mobileMenu = document.getElementById('mobile-menu');
  if (toggle && mobileMenu) {
    const setMobileOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? 'Close' : 'Menu';
      mobileMenu.hidden = !open;
    };
    toggle.addEventListener('click', () => setMobileOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    // Close after a choice, including links to a part of the same page.
    mobileMenu.addEventListener('click', (event) => {
      if (event.target.closest('a')) setMobileOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (!toggle.isConnected || mobileMenu.hidden) return;
      if (event.key === 'Escape') {
        setMobileOpen(false);
        toggle.focus();
        return;
      }
      // While the menu is open, Tab moves only between the Close button and the menu.
      if (event.key === 'Tab') {
        const items = [toggle, ...mobileMenu.querySelectorAll('a[href], button:not([hidden])')].filter((item) => item.offsetParent !== null);
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!items.includes(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  }
}

/* Collapsed sections: a link to one (e.g. #state-services) opens it -------- */

function openLinkedDetails() {
  const id = decodeURIComponent(window.location.hash.slice(1));
  const target = id ? document.getElementById(id) : null;
  const details = target?.closest('details');
  if (details) {
    details.open = true;
    details.scrollIntoView();
  }
}

/* Enquiry form ------------------------------------------------------------- */
// Pre-selects the enquiry type and service from links such as
// /pages/contact?enquiry=fit-call&service=couples#enquiry-form.

function initEnquiryForm() {
  const form = document.querySelector('form.enquiry-form');
  if (!form) return;
  const params = new URLSearchParams(window.location.search);

  const enquiry = params.get('enquiry');
  if (enquiry) {
    const radio = form.querySelector(`#enquiry-${CSS.escape(enquiry)}`);
    if (radio) radio.checked = true;
  }

  const service = params.get('service');
  if (service) {
    const option = form.querySelector(`option[data-key="${CSS.escape(service)}"]`);
    if (option) option.selected = true;
  }

  // State and suburb are asked only of people who may attend online.
  const format = form.querySelector('[data-format]');
  const location = form.querySelector('[data-online-location]');
  if (format && location) {
    const update = () => {
      location.hidden = !/online|either/i.test(format.value);
    };
    format.addEventListener('change', update);
    update();
  }

  initFormErrors(form);

  // With Theme settings > Practice system switched on, a copy of each enquiry also goes to
  // the practice system's inbox. Shopify still sends its email as usual.
  const practice = form.closest('[data-practice-endpoint]')?.dataset.practiceEndpoint;
  if (practice && !form.dataset.practiceCopy) {
    form.dataset.practiceCopy = 'on';
    form.addEventListener('submit', () => {
      try {
        fetch(`${practice.replace(/\/+$/, '')}/api/public/enquiries`, {
          method: 'POST',
          body: new FormData(form),
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
        }).catch(() => {});
      } catch (error) {
        // The Shopify email is the record of last resort; never block the form.
      }
    });
  }

  // After Shopify has sent the form, bring the thank-you or error message into view.
  const status = form.querySelector('[data-form-status]');
  if (status && !window.Shopify?.designMode) {
    status.scrollIntoView({ block: 'center' });
    status.focus({ preventScroll: true });
  }
}

// Specific error messages, tied to each field and announced, instead of the browser's pop-ups.
function initFormErrors(form) {
  const summary = form.querySelector('[data-error-summary]');
  if (!summary || form.dataset.errorsReady) return;
  form.dataset.errorsReady = 'on';
  form.noValidate = true;
  const clean = (text) => (text || '').replace(/\s*\*\s*$/, '').trim();
  const labelFor = (field) =>
    field.type === 'radio'
      ? clean(field.closest('fieldset')?.querySelector('legend')?.textContent)
      : clean(form.querySelector(`label[for="${field.id}"]`)?.textContent || field.name);
  const problem = (field) => {
    if (field.validity.valueMissing) return field.type === 'radio' ? 'Please choose one option.' : 'Please fill in this field.';
    if (field.validity.typeMismatch && field.type === 'email') return 'Please enter an email address, like name@example.com.';
    return field.validationMessage;
  };
  const clear = () => {
    form.querySelectorAll('.field-error').forEach((node) => node.remove());
    form.querySelectorAll('[aria-invalid]').forEach((node) => {
      node.removeAttribute('aria-invalid');
      const ids = (node.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && !id.endsWith('-error'));
      if (ids.length) node.setAttribute('aria-describedby', ids.join(' '));
      else node.removeAttribute('aria-describedby');
    });
    summary.hidden = true;
    summary.textContent = '';
  };
  form.addEventListener('submit', (event) => {
    clear();
    const seen = new Set();
    const errors = [];
    form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (!field.name || field.checkValidity() || seen.has(field.name)) return;
      seen.add(field.name);
      const text = problem(field);
      const id = `${field.id || field.name.replace(/\W+/g, '-')}-error`;
      const message = document.createElement('p');
      message.className = 'field-error';
      message.id = id;
      message.textContent = text;
      const group = field.type === 'radio' ? form.querySelectorAll(`input[name="${CSS.escape(field.name)}"]`) : [field];
      group.forEach((input) => {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', `${input.getAttribute('aria-describedby') || ''} ${id}`.trim());
      });
      (field.type === 'radio' ? field.closest('fieldset').querySelector('legend') : field).insertAdjacentElement('afterend', message);
      errors.push({ field, label: labelFor(field), text });
    });
    if (!errors.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const heading = document.createElement('p');
    heading.textContent = errors.length === 1 ? 'Please check 1 field:' : `Please check ${errors.length} fields:`;
    const list = document.createElement('ul');
    errors.forEach(({ label, text }) => {
      const item = document.createElement('li');
      item.textContent = `${label}: ${text}`;
      list.append(item);
    });
    summary.replaceChildren(heading, list);
    summary.hidden = false;
    errors[0].field.focus();
  });
}

/* Listeners ---------------------------------------------------------------- */

window.addEventListener('hashchange', openLinkedDetails);

// Theme editor: re-run after a section reloads.
document.addEventListener('shopify:section:load', () => {
  initHeader();
  initEnquiryForm();
});

// Theme editor: open the question or group selected in the sidebar.
document.addEventListener('shopify:block:select', (event) => {
  if (event.target.matches?.('details')) event.target.open = true;
});

initHeader();
initEnquiryForm();
openLinkedDetails();
